"use client";

import { useMemo } from "react";
import { ProbeHistoryPoint } from "@/lib/aiModelPricing";

interface Props {
  history: ProbeHistoryPoint[];
  height?: number;
}

/**
 * 30-day probe-history sparkline. Plots per-day ok-rate as a green/red bar
 * (1=ok, 0=fail) and overlays a faint latency line. Reusable across all
 * three admin portals.
 */
export function Sparkline({ history, height = 80 }: Props) {
  const { bars, line, width } = useMemo(() => {
    const buckets = new Map<string, { ok: number; total: number; latencyMs: number }>();
    for (const p of history) {
      const day = p.probedAt.slice(0, 10);
      const cur = buckets.get(day) ?? { ok: 0, total: 0, latencyMs: 0 };
      cur.ok += p.ok ? 1 : 0;
      cur.total += 1;
      cur.latencyMs = Math.max(cur.latencyMs, p.latencyMs);
      buckets.set(day, cur);
    }
    const days = Array.from(buckets.entries()).sort();
    const w = 360;
    const barWidth = Math.max(2, Math.min(10, Math.floor(w / Math.max(days.length, 1))));

    const bars = days.map(([_day, v], i) => {
      const rate = v.total > 0 ? v.ok / v.total : 0;
      return {
        x: i * (w / Math.max(days.length, 1)),
        h: Math.max(4, Math.floor(rate * (height - 12))),
        ok: rate > 0.5,
        latency: v.latencyMs,
      };
    });

    const maxLat = Math.max(...days.map(([, v]) => v.latencyMs), 1);
    const line = days.map(([_day, v], i) => ({
      x: i * (w / Math.max(days.length, 1)) + barWidth / 2,
      y: height - 4 - Math.floor((v.latencyMs / maxLat) * (height - 12)),
    }));

    return { bars, line, width: w };
  }, [history, height]);

  if (history.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-[11px] text-[#8E8E93] font-mono"
        style={{ height }}
      >
        No probe history yet — waiting for the daily cron.
      </div>
    );
  }

  const linePath = line
    .map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`)
    .join(" ");

  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} className="block">
      {bars.map((b, i) => (
        <rect
          key={i}
          x={b.x}
          y={height - 4 - b.h}
          width={Math.max(2, b.h / 2)}
          height={b.h}
          rx={1}
          fill={b.ok ? "#10b981" : "#E63946"}
          opacity={0.85}
        />
      ))}
      <path
        d={linePath}
        fill="none"
        stroke="#FFD60A"
        strokeWidth={1.2}
        strokeLinejoin="round"
        strokeLinecap="round"
        opacity={0.6}
      />
    </svg>
  );
}
